/**
 *
 * IMPORTANT NOTE: When we press the "del" key on a 'header-button' cell, we get a strange error. It doesn't break anything, but it's something we should check.
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Fade } from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import { CircularIndeterminate, DataGrid } from '@utriper/core-ui';
import { BookingOrigin } from '@utriper/core';
import { getColumns, getRows, getHeaderRow, getQuantityLabel } from './utils';
import { FORM_IDS } from 'modules/Pricing/PriceGroupId/styles';

const PriceGroupGrid = ({ data = {}, ui = {}, actions = {}, config = {} }) => {
    const { prices = [], units = [], bookingOrigins = [] } = data;
    const { loading = false } = ui;
    const { handleUpdatePrice = () => {}, handleOpenEdit = () => {}, handleOpenDelete = () => {}, setPrices = () => {} } = actions;
    const { canEdit = false, hideDefaultOrigin = false } = config;
    const { t, i18n } = useTranslation();
    const translate = (key, options) => t(`price_group_grid.${key}`, options);

    // STATES
    const [activeColumn, setActiveColumn] = useState(null);
    const [activeRow, setActiveRow] = useState(null);

    // MEMOS
    const headerType = useMemo(() => (canEdit ? 'header-button' : 'header'), [canEdit]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const columns = useMemo(() => getColumns(hideDefaultOrigin, units, bookingOrigins, { translate, language: i18n.language }), [units, bookingOrigins, hideDefaultOrigin, i18n.language]);

    const rows = useMemo(() => {
        const translateOptions = { translate, language: i18n.language };
        if (Array.isArray(prices) && prices.length > 0 && Array.isArray(units) && units?.length > 0) {
            return getRows(prices, headerType, canEdit, hideDefaultOrigin, handleOpenEdit, units, bookingOrigins, translateOptions);
        }

        return getHeaderRow(headerType, canEdit, hideDefaultOrigin, handleOpenEdit, units, bookingOrigins, translateOptions);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prices, units, bookingOrigins, headerType, canEdit, hideDefaultOrigin, handleOpenEdit, i18n.language]);

    const canDeleteUnits = useMemo(() => canEdit && Array.isArray(units) && units?.length > 1, [canEdit, units]);

    const canDeleteRanges = useMemo(() => canEdit && rows?.length > 3 && Array.isArray(prices) && prices?.length > 0, [canEdit, rows, prices]);

    const canDeleteOrigins = useMemo(() => canEdit && Array.isArray(bookingOrigins) && bookingOrigins?.length > 0, [canEdit, bookingOrigins]);

    const highlights = useMemo(() => {
        if (!canEdit) return [];

        const className = 'active-column';

        if (Number.isInteger(activeColumn?.unitId)) {
            const headerGroupRow = {
                rowId: 'headerGroup',
                columnId: activeColumn.id,
                className,
            };

            const columnIds = columns?.filter((col) => col?.unitId === activeColumn.unitId)?.map((col) => col?.columnId) ?? [];
            let headerRows = [];
            if (columnIds?.length > 0) {
                for (const columnId of columnIds) {
                    const rowsToAdd =
                        rows
                            ?.filter((row) => row?.rowId !== 'headerGroup')
                            ?.map((row) => ({
                                rowId: row?.rowId,
                                columnId,
                                className,
                            })) ?? [];

                    headerRows = headerRows.concat(rowsToAdd);
                }
            }

            return [headerGroupRow, ...headerRows];
        }

        if (Number.isInteger(activeColumn?.bookingOriginId)) {
            const columnIds = columns?.filter((col) => col?.bookingOriginId === activeColumn.bookingOriginId)?.map((col) => col?.columnId) ?? [];
            let headerRows = [];
            if (columnIds?.length > 0) {
                for (const columnId of columnIds) {
                    const rowsToAdd = rows
                        ?.filter((row) => row?.rowId !== 'headerGroup')
                        ?.map((row) => ({
                            rowId: row?.rowId,
                            columnId,
                            className,
                        }));

                    headerRows = headerRows.concat(rowsToAdd);
                }
            }

            return headerRows;
        }

        if (Number.isInteger(activeRow?.rowId)) {
            return columns?.map((column) => ({
                rowId: activeRow?.rowId,
                columnId: column?.columnId,
                className,
            }));
        }

        return [];
    }, [canEdit, activeColumn, activeRow, rows, columns]);

    const cellFocusStyles = useMemo(() => {
        if (!activeColumn?.id) return {};

        // THIS IS TO REMOVE THE BORDER FROM RANGE CELL
        if (activeColumn?.isHeaderGroup) {
            return {
                '& .rg-cell-focus': {
                    border: 'none !important',
                },
            };
        }

        if (!activeColumn?.unitId || !activeColumn?.width || !canEdit) return {};

        return {
            '& .rg-cell-focus': {
                width: `${activeColumn?.width}px !important`,
            },
        };
    }, [activeColumn, canEdit]);

    // UTILS
    const handleChangePrices = (changes = [], prevDetails = []) => {
        for (const { rowId, type, newCell } of changes) {
            if (type !== 'number') continue;

            const dataRow = prevDetails?.find((prev) => prev?.rowId === rowId);
            if (!dataRow?.prices) continue;

            const { value, bookingOriginId, unitId } = newCell;
            if (!unitId || !value) continue;

            const priceToEdit = dataRow?.prices?.find((price) => price?.unitId === unitId && price?.bookingOriginId === bookingOriginId);
            priceToEdit.price = value;

            // SET NEW PRICE
            if (typeof handleUpdatePrice === 'function') {
                handleUpdatePrice({ ...priceToEdit });
            }
        }
    };

    const handleChangeDataGrid = (changes) => {
        if (typeof setPrices === 'function') {
            setPrices((prevPrices) => {
                const updatedPrices = [...prevPrices];
                handleChangePrices(changes, updatedPrices);

                return updatedPrices;
            });
        }
    };

    const handleSelectionChange = (selection) => {
        if (!canEdit) return;

        const first = selection?.[0]?.first;
        if (!first) return;

        const { row, column } = first;

        if (column?.columnId && !Number.isInteger(column?.unitId) && canDeleteRanges) {
            // THIS IS TO REMOVE THE BORDER FROM RANGE CELL
            if (row?.rowId === 'headerGroup') {
                const rangeColumn = {
                    id: column.columnId,
                    name: '',
                    isHeaderGroup: true,
                };
                setActiveColumn(rangeColumn);
                setActiveRow(null);
                return;
            }

            const lastRow = rows[rows?.length - 1];

            if (rows?.length > 3 && Number.isInteger(lastRow?.rowId) && lastRow.rowId === row?.rowId) {
                const rangeRow = {
                    rowId: row.rowId,
                    quantityFrom: row.quantityFrom,
                    quantityTo: row.quantityTo,
                    label: getQuantityLabel(row.quantityFrom, row.quantityTo, translate),
                };
                setActiveRow({ ...rangeRow });
                setActiveColumn(null);
                return;
            }
            setActiveRow(null);
            setActiveColumn(null);
            return;
        }

        if (row?.rowId === 'headerGroup' && column?.unitId && canDeleteUnits) {
            const unit = units?.find((unit) => unit?.id === column?.unitId) ?? '';
            const originsLength = 1 + (bookingOrigins?.length ?? 0);
            const width = column?.width * originsLength;
            const unitColumn = { id: column.columnId, name: unit?.name, unitId: column?.unitId, width };

            setActiveColumn({ ...unitColumn });
            setActiveRow(null);
            return;
        }

        if (row?.rowId === 'header' && column?.bookingOriginId && canDeleteOrigins) {
            const originColumn = { id: column.columnId, name: column.name, bookingOriginId: column?.bookingOriginId };
            setActiveColumn({ ...originColumn });
            setActiveRow(null);
            return;
        }

        if (Number.isInteger(row?.rowId) || (row?.rowId === 'header' && !column?.bookingOriginId)) {
            setActiveRow(null);
            setActiveColumn(null);
            return;
        }
    };

    // TODO: Check if it is convenient to only set display: "none" when loading is true to not re-render the grid
    if (loading) {
        return <CircularIndeterminate centered />;
    }

    return (
        <Box component="section" sx={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: '16px' }}>
            {/* GRID */}
            <Box
                width="100%"
                maxWidth="1440px"
                maxHeight="768px"
                overflow="auto"
                mt={1}
                sx={{
                    '& .rg-header-button-cell': { backgroundColor: '#F2F0ED' },
                    '& .rg-header-cell': { backgroundColor: '#F2F0ED !important' },
                    '& .rg-header-text-cell': { backgroundColor: '#F2F0ED !important' },
                    '& .active-column': {
                        border: '1px solid #0683f9 !important',
                        backgroundColor: '#0683f940',
                    },
                    ...cellFocusStyles,
                }}
            >
                <DataGrid rows={rows} columns={columns} highlights={highlights} handleChange={handleChangeDataGrid} onSelectionChanged={handleSelectionChange} stickyLeftColumns={1} />
            </Box>

            {/* Delete Unit */}
            {Number.isInteger(activeColumn?.unitId) && canDeleteUnits && (
                <Fade in={Number.isInteger(activeColumn.unitId)}>
                    <Button
                        variant="text"
                        color="error"
                        startIcon={<DeleteOutline />}
                        sx={{
                            textTransform: 'none',
                        }}
                        onClick={() => handleOpenDelete(FORM_IDS.DELETE_UNIT, activeColumn)}
                    >
                        {translate('delete_label', { origin: activeColumn?.name })}
                    </Button>
                </Fade>
            )}

            {/* Delete Origin */}
            {Number.isInteger(activeColumn?.bookingOriginId) && canDeleteOrigins && (
                <Fade in={Number.isInteger(activeColumn.bookingOriginId)}>
                    <Button
                        variant="text"
                        color="error"
                        startIcon={<DeleteOutline />}
                        sx={{
                            textTransform: 'none',
                        }}
                        onClick={() => handleOpenDelete(FORM_IDS.DELETE_ORIGIN, activeColumn)}
                    >
                        {translate('delete_label', { origin: BookingOrigin.getTranslatedName(BookingOrigin.types, activeColumn.bookingOriginId, i18n?.language)?.[0] })}
                    </Button>
                </Fade>
            )}

            {/* Delete Range */}
            {Number.isInteger(activeRow?.rowId) && canDeleteRanges && (
                <Fade in={Number.isInteger(activeRow?.rowId)}>
                    <Button
                        variant="text"
                        color="error"
                        startIcon={<DeleteOutline />}
                        sx={{
                            textTransform: 'none',
                        }}
                        onClick={() => handleOpenDelete(FORM_IDS.DELETE_RANGE, activeRow)}
                    >
                        {translate('delete_label', { origin: activeRow?.label })}
                    </Button>
                </Fade>
            )}
        </Box>
    );
};

export default PriceGroupGrid;
